import { OBJExporter } from "three/examples/jsm/exporters/OBJExporter";

const MIME_EXT = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
const STEM = "model";

// Parse a GLB ArrayBuffer into its glTF JSON + binary chunk.
// GLB layout: 12-byte header, then chunks [uint32 length, uint32 type, data].
// Chunk type 0x4E4F534A = "JSON", 0x004E4942 = "BIN".
function parseGlb(glb) {
  const view = new DataView(glb);
  if (view.getUint32(0, true) !== 0x46546c67) return null; // not "glTF"
  let offset = 12;
  let json = null;
  let bin = null;
  while (offset < view.byteLength) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const start = offset + 8;
    if (type === 0x4e4f534a) {
      json = JSON.parse(new TextDecoder().decode(new Uint8Array(glb, start, length)));
    } else if (type === 0x004e4942) {
      bin = new Uint8Array(glb, start, length);
    }
    offset = start + length;
  }
  return json ? { json, bin } : null;
}

// Return the original bytes + mime for each glTF image embedded via bufferView.
// URI-based images yield null at that index (caller falls back to canvas).
function extractEmbeddedImages({ json, bin }) {
  const images = json.images || [];
  const views = json.bufferViews || [];
  return images.map((img) => {
    if (img.bufferView == null || !bin) return null;
    const bv = views[img.bufferView];
    const byteOffset = bv.byteOffset || 0;
    const bytes = bin.slice(byteOffset, byteOffset + bv.byteLength);
    const mimeType = img.mimeType || "image/png";
    return { blob: new Blob([bytes], { type: mimeType }), mimeType };
  });
}

// Canvas fallback: rasterize a live Three texture map to a PNG blob.
async function textureToPngBlob(map) {
  const image = map?.image;
  if (!image) return null;
  const canvas = document.createElement("canvas");
  canvas.width = image.width || image.videoWidth;
  canvas.height = image.height || image.videoHeight;
  canvas.getContext("2d").drawImage(image, 0, 0);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

function collectMaterials(object3D) {
  const seen = new Set();
  const materials = [];
  object3D.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const list = Array.isArray(child.material) ? child.material : [child.material];
    list.forEach((m) => {
      if (seen.has(m.uuid)) return;
      seen.add(m.uuid);
      materials.push(m);
    });
  });
  return materials;
}

function colorLine(material) {
  const c = material.color;
  return c ? `Kd ${c.r.toFixed(6)} ${c.g.toFixed(6)} ${c.b.toFixed(6)}` : "Kd 1 1 1";
}

export async function buildObjFiles({ object3D, glb }, folder) {
  const materials = collectMaterials(object3D);

  // Give every material a stable, unique name so `usemtl`/`newmtl` line up.
  const usedNames = new Set();
  materials.forEach((m, i) => {
    let name = m.name || `material_${i}`;
    while (usedNames.has(name)) name = `${name}_${i}`;
    m.name = name;
    usedNames.add(name);
  });

  // Geometry (.obj) — reflects the model's current in-scene transform.
  let objText = new OBJExporter().parse(object3D);
  objText = `mtllib ${STEM}.mtl\n${objText}`;

  // Textures. A deliverable is almost always a single texture atlas shared by
  // every surface: extract its original bytes from the GLB once and reuse them.
  // Anything else (multiple images, or non-embedded/URI images) is rasterized
  // from the live material — correct pixels, re-encoded to PNG.
  const parsed = parseGlb(glb);
  const embedded = parsed ? extractEmbeddedImages(parsed) : [];
  const soleAtlas = embedded.length === 1 && embedded[0] ? embedded[0] : null;

  const files = [];
  const imageFileByMatName = new Map();
  const pngByTextureUuid = new Map(); // dedupe canvas re-encodes of a shared map
  let atlasFileName = null;
  let counter = 0;

  for (const material of materials) {
    if (!material.map) continue; // colour-only material — no image
    let fileName;
    if (soleAtlas) {
      // Emit the shared atlas once (exact original bytes), reuse thereafter.
      if (!atlasFileName) {
        atlasFileName = `${STEM}_${counter}.${MIME_EXT[soleAtlas.mimeType] || "png"}`;
        counter += 1;
        files.push({ name: `${folder}/${atlasFileName}`, blob: soleAtlas.blob });
      }
      fileName = atlasFileName;
    } else {
      const uuid = material.map.uuid;
      if (pngByTextureUuid.has(uuid)) {
        fileName = pngByTextureUuid.get(uuid);
      } else {
        const blob = await textureToPngBlob(material.map);
        if (!blob) continue;
        fileName = `${STEM}_${counter}.png`;
        counter += 1;
        files.push({ name: `${folder}/${fileName}`, blob });
        pngByTextureUuid.set(uuid, fileName);
      }
    }
    imageFileByMatName.set(material.name, fileName);
  }

  // Material library (.mtl).
  const mtlText = materials
    .map((m) => {
      const lines = [`newmtl ${m.name}`, colorLine(m)];
      const img = imageFileByMatName.get(m.name);
      if (img) lines.push(`map_Kd ${img}`);
      return lines.join("\n");
    })
    .join("\n\n");

  return [
    { name: `${folder}/${STEM}.obj`, blob: new Blob([objText], { type: "text/plain" }) },
    { name: `${folder}/${STEM}.mtl`, blob: new Blob([mtlText], { type: "text/plain" }) },
    ...files,
  ];
}
